import { lazy, Suspense, useEffect, type ComponentType, type ReactNode } from 'react'
import { generatePath, matchPath, Navigate, Route, Routes, useLocation, useParams } from 'react-router'

import { MarkNotice } from './components/MarkNotice'
import { forgetNamedClasses } from './annotation/namedClasses'
import { ResearchNotice } from './components/ResearchNotice'
import { Sidebar } from './components/Sidebar'
import { NotFound } from './components/ui'
import { forgetImpactSessions } from './impact/kept'
import { useI18n } from './lib/i18n'
import { SessionGate, useSession } from './lib/session'
import { TokenProvider } from './lib/token'
import { AssignmentsPage } from './pages/AssignmentsPage'
import { LegalPage, TermsGate } from './pages/LegalPage'
import { NotificationsPage } from './pages/NotificationsPage'
import { UploadDock } from './pages/uploads/UploadDock'
import { DropUploads } from './upload/DropUploads'
import { UploadsProvider } from './upload/UploadsProvider'

/** A page loaded on its first visit, so the first screen does not wait for the viewer or the workspace. */
// `any` is React.lazy's own constraint; the props stay the page's.
function page<Name extends string, Module extends Record<Name, ComponentType<any>>>(load: () => Promise<Module>, name: Name) {
  return lazy(() => load().then((module) => ({ default: module[name] })))
}

const AdjudicationStart = page(() => import('./pages/ComparisonPage'), 'AdjudicationStart')
const AuditPage = page(() => import('./pages/AuditPage'), 'AuditPage')
const CollectionPage = page(() => import('./collections/CollectionPage'), 'CollectionPage')
const CollectionsPage = page(() => import('./collections/CollectionsPage'), 'CollectionsPage')
const ContributionsPage = page(() => import('./pages/ContributionsPage'), 'ContributionsPage')
const ComparisonPage = page(() => import('./pages/ComparisonPage'), 'ComparisonPage')
const CoursePage = page(() => import('./pages/education/CoursePage'), 'CoursePage')
const FrozenCollectionPage = page(() => import('./pages/FrozenCollectionPage'), 'FrozenCollectionPage')
const FrozenImagesPage = page(() => import('./pages/FrozenCollectionPage'), 'FrozenImagesPage')
const DevicePage = page(() => import('./pages/DevicePage'), 'DevicePage')
const EducationPage = page(() => import('./pages/education/EducationPage'), 'EducationPage')
const BenchmarkPage = page(() => import('./pages/benchmarks/BenchmarkPage'), 'BenchmarkPage')
const BenchmarksPage = page(() => import('./pages/benchmarks/BenchmarksPage'), 'BenchmarksPage')
const CatalogPage = page(() => import('./catalog/CatalogPage'), 'CatalogPage')
const LabelingProfilesPage = page(() => import('./labeling/LabelingProfilesPage'), 'LabelingProfilesPage')
const loadGallery = () => import('./gallery/GalleryPage')
// Most visits start in the gallery, so its code loads beside the session rather than after it; a failure
// shows once the page itself asks for it.
if (window.location.pathname === '/' || matchPath('/images', window.location.pathname)) loadGallery().catch(() => {})
const GalleryPage = page(loadGallery, 'GalleryPage')
const ForumPage = page(() => import('./forum/ForumPage'), 'ForumPage')
const PollsPage = page(() => import('./scheduling/PollsPage'), 'PollsPage')
const PollPage = page(() => import('./scheduling/PollPage'), 'PollPage')
const SharedPollPage = page(() => import('./scheduling/PollPage'), 'SharedPollPage')
const ForumModerationPage = page(() => import('./forum/ModerationPage'), 'ModerationPage')
const ForumTopicPage = page(() => import('./forum/TopicPage'), 'TopicPage')
const GuidelinesPage = page(() => import('./pages/GuidelinesPage'), 'GuidelinesPage')
const HelpPage = page(() => import('./pages/HelpPage'), 'HelpPage')
const ImagePage = page(() => import('./pages/ImagePage'), 'ImagePage')
const ImpactPage = page(() => import('./impact/ImpactPage'), 'ImpactPage')
const AssignmentPage = page(() => import('./pages/AssignmentPage'), 'AssignmentPage')
const ModelsPage = page(() => import('./pages/ModelsPage'), 'ModelsPage')
const PatientPage = page(() => import('./pages/PatientPage'), 'PatientPage')
const ProfilePage = page(() => import('./pages/ProfilePage'), 'ProfilePage')
const PersonPage = page(() => import('./people/PersonPage'), 'PersonPage')
const AccountsPage = page(() => import('./people/AccountsPage'), 'AccountsPage')
const QuizPage = page(() => import('./pages/education/QuizPage'), 'QuizPage')
const ReviewPage = page(() => import('./pages/ReviewPage'), 'ReviewPage')
const SecondFactorPage = page(() => import('./pages/SecondFactorPage'), 'SecondFactorPage')
const SharedPage = page(() => import('./pages/SharedPage'), 'SharedPage')
const SitePage = page(() => import('./pages/SitePage'), 'SitePage')
const SitesPage = page(() => import('./pages/SitesPage'), 'SitesPage')
const SpeechBenchPage = page(() => import('./pages/SpeechBenchPage'), 'SpeechBenchPage')
const StatusPage = page(() => import('./pages/StatusPage'), 'StatusPage')
const JobPage = page(() => import('./pages/JobPage'), 'JobPage')
const UploadsPage = page(() => import('./pages/uploads/UploadsPage'), 'UploadsPage')
const WorkPage = page(() => import('./pages/WorkPage'), 'WorkPage')

export function App() {
  const { t } = useI18n()
  return (
    <>
      <Routes>
        {/* Legal texts need no login, so Keycloak's pages link to them too (NTF-04). */}
        <Route path="/legal/:document" element={<LegalPage />} />
        <Route path="/poll/:token" element={<Suspense fallback={<p className="muted center">{t('loading')}</p>}><SharedPollPage /></Suspense>} />
        <Route path="*" element={<SignedIn />} />
      </Routes>
      <ResearchNotice />
      <MarkNotice />
    </>
  )
}

function SignedIn() {
  const { t } = useI18n()
  return (
    <SessionGate>
      <TermsAccepted>
        <TokenProvider>
          <UploadsProvider>
            <DropUploads />
            <Housekeeping />
            <Shell>
              <Suspense fallback={<p className="muted">{t('loading')}</p>}>
                <Routes>
                  <Route path="/" element={<Navigate to="/images" replace />} />
                  <Route path="/uploads" element={<UploadsPage />} />
                  <Route path="/uploads/:jobId" element={<UploadsPage />} />
                  <Route path="/collections" element={<CollectionsPage />} />
                  <Route path="/collections/:collectionId" element={<CollectionPage />} />
                  <Route path="/guidelines" element={<GuidelinesPage />} />
                  <Route path="/images" element={<GalleryPage />} />
                  <Route path="/images/:imageId" element={<ImagePage />} />
                  <Route path="/impact" element={<ImpactPage />} />
                  <Route path="/assignments" element={<AssignmentsPage />} />
                  <Route path="/jobs" element={<Navigate to="/assignments" replace />} />
                  <Route path="/jobs/:assignmentId" element={<Renamed to="/assignments/:assignmentId" />} />
                  <Route path="/tasks/:jobId" element={<Renamed to="/annotation-jobs/:jobId" />} />
                  <Route path="/tasks/:jobId/images/:imageId" element={<Renamed to="/annotation-jobs/:jobId/images/:imageId" />} />
                  <Route path="/contributions/:siteId" element={<ContributionsPage />} />
                  <Route path="/assignments/:assignmentId" element={<AssignmentPage />} />
                  <Route path="/reviews/:assignmentId" element={<ReviewPage />} />
                  <Route path="/annotation-jobs/:jobId" element={<JobPage />} />
                  <Route path="/annotation-jobs/:jobId/images/:imageId" element={<ComparisonPage />} />
                  <Route path="/adjudicate/:jobId" element={<AdjudicationStart />} />
                  <Route path="/adjudicate/:jobId/:imageId" element={<ComparisonPage adjudicating />} />
                  <Route path="/models" element={<ModelsPage />} />
                  <Route path="/catalog" element={<CatalogPage />} />
                  <Route path="/labeling-profiles" element={<LabelingProfilesPage />} />
                  <Route path="/audit" element={<AuditPage />} />
                  <Route path="/status" element={<StatusPage />} />
                  <Route path="/speech-bench" element={<SpeechBenchPage />} />
                  <Route path="/notifications" element={<NotificationsPage />} />
                  <Route path="/shared" element={<SharedPage />} />
                  <Route path="/education" element={<EducationPage />} />
                  <Route path="/education/courses/:courseId" element={<CoursePage />} />
                  <Route path="/education/:setId" element={<QuizPage />} />
                  <Route path="/frozen-collections/:frozenCollectionId" element={<FrozenCollectionPage />} />
                  <Route path="/frozen-collections/:frozenCollectionId/images/:imageId" element={<FrozenImagesPage />} />
                  <Route path="/datasets/:frozenCollectionId" element={<Renamed to="/frozen-collections/:frozenCollectionId" />} />
                  <Route path="/datasets/:frozenCollectionId/images/:imageId" element={<Renamed to="/frozen-collections/:frozenCollectionId/images/:imageId" />} />
                  <Route path="/benchmarks" element={<BenchmarksPage />} />
                  <Route path="/forum" element={<ForumPage />} />
                  <Route path="/scheduling" element={<PollsPage />} />
                  <Route path="/scheduling/:pollId" element={<PollPage />} />
                  <Route path="/forum/t/:topicId" element={<ForumTopicPage />} />
                  <Route path="/forum/moderation" element={<ForumModerationPage />} />
                  <Route path="/benchmarks/:benchmarkId" element={<BenchmarkPage />} />
                  {/* The sites workspace: the site chosen, with the switcher to the user's other sites; the work organized there under /work. */}
                  <Route path="/sites" element={<SitesPage />}>
                    <Route path=":siteId" element={<SitePage />} />
                  </Route>
                  <Route path="/work" element={<SitesPage area="work" />}>
                    <Route path=":siteId" element={<WorkPage />} />
                  </Route>
                  <Route path="/sites/:siteId/patients" element={<PatientPage />} />
                  <Route path="/second-factor" element={<SecondFactorPage />} />
                  <Route path="/profile" element={<ProfilePage />} />
                  <Route path="/people/:userId" element={<PersonPage />} />
                  <Route path="/accounts" element={<AccountsPage />} />
                  <Route path="/token-check" element={<Navigate to="/sites" replace />} />
                  <Route path="/studies" element={<Navigate to="/images" replace />} />
                  <Route path="/device" element={<DevicePage />} />
                  <Route path="/help" element={<HelpPage />} />
                  <Route path="*" element={<NotFound />} />
                </Routes>
              </Suspense>
            </Shell>
          </UploadsProvider>
        </TokenProvider>
      </TermsAccepted>
    </SessionGate>
  )
}

/** Nothing of the platform, not even its background requests, before its terms of use are accepted (AUTH-10). */
function TermsAccepted({ children }: { children: ReactNode }) {
  const { me } = useSession()
  return me.terms_accepted ? children : <TermsGate />
}

/** Pages that list images use the full window width, with the navigation folded to a rail. */
const WIDE_PAGES = ['/images', '/collections/:collectionId']

/**
 * Every page that shows images is the reading workspace, which fills the window above the research
 * notice, as the standalone app does; its brand and menu lead back.
 */
const READING_PAGES = [
  '/assignments/:assignmentId',
  '/images/:imageId',
  '/reviews/:assignmentId',
  '/annotation-jobs/:jobId/images/:imageId',
  '/adjudicate/:jobId/:imageId',
  '/frozen-collections/:frozenCollectionId/images/:imageId',
  '/education/:setId',
]

/**
 * Pages that use the whole width beside the navigation, laying out more side by side as it grows: uploads with
 * their lists of files, a site's boards of cards, the collections' tiles, the labeling profiles and the catalog
 * beside their labels, the reader's jobs, the forum, meeting polls with their dates and results, grants, the
 * profile's sections, the users directory beside the selected user's profile. Their prose and forms keep a readable
 * width of their own.
 */
const FULL_PAGES = [
  '/uploads',
  '/uploads/:jobId',
  '/sites',
  '/sites/:siteId',
  '/work',
  '/work/:siteId',
  '/collections',
  '/labeling-profiles',
  '/catalog',
  '/assignments',
  '/forum',
  '/forum/t/:topicId',
  '/scheduling',
  '/scheduling/:pollId',
  '/shared',
  '/profile',
  '/accounts',
]

/** Once per sign-in: only this user's named free-text classes (LBL-08) and impact sessions (ANN-36) stay in this browser. */
function Housekeeping() {
  const { me } = useSession()
  useEffect(() => {
    forgetNamedClasses(me.id)
    forgetImpactSessions(me.id)
  }, [me.id])
  return null
}

function Shell({ children }: { children: ReactNode }) {
  const { me } = useSession()
  const { pathname } = useLocation()
  const wide = WIDE_PAGES.some((pattern) => matchPath(pattern, pathname))
  const full = FULL_PAGES.some((pattern) => matchPath(pattern, pathname))
  const reading = READING_PAGES.some((pattern) => matchPath(pattern, pathname))
  // Specialty and experience come before anything else (AUTH-12), after the terms of use (`TermsAccepted`).
  const gated = !me.profile_complete && pathname !== '/profile' ? <Navigate to="/profile" replace /> : undefined

  if (reading && !gated) return <div className="reader-host">{children}</div>
  return (
    <div className={wide ? 'app-shell collapsed' : 'app-shell'}>
      <Sidebar collapsed={wide} />
      <div className="app-main">
        <main className={wide ? 'container wide' : full ? 'container full' : 'container'}>
          {gated ?? children}
        </main>
        {!gated && <UploadDock />}
      </div>
    </div>
  )
}

/** Links from before annotation jobs, assignments and frozen collections had their names, as in e-mails, lead to the same page. */
function Renamed({ to }: { to: string }) {
  return <Navigate to={generatePath(to, useParams())} replace />
}
